import { useState, useCallback, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import TaskService from '../../../../services/task_service';
import { TenantContext } from '../../../../contexts/TenantContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TASK_STATUS, TASK_STATUS_LABELS, TASK_TYPE } from '../../../../constants/tasks';

export const useOperationalKanban = (refreshKey) => {
    const { idTenant, user } = useContext(TenantContext);

    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState({
        [TASK_STATUS.NEW]: { id: TASK_STATUS.NEW, title: TASK_STATUS_LABELS[TASK_STATUS.NEW], items: [] },
        [TASK_STATUS.DOING]: { id: TASK_STATUS.DOING, title: TASK_STATUS_LABELS[TASK_STATUS.DOING], items: [] },
        [TASK_STATUS.OVERDUE]: { id: TASK_STATUS.OVERDUE, title: TASK_STATUS_LABELS[TASK_STATUS.OVERDUE], items: [] },
        [TASK_STATUS.DONE]: { id: TASK_STATUS.DONE, title: TASK_STATUS_LABELS[TASK_STATUS.DONE], items: [] }
    });

    const fetchTasks = useCallback(async () => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const fetchedTasks = await TaskService.getTasks(idTenant);

            const newColumns = {
                [TASK_STATUS.NEW]: { id: TASK_STATUS.NEW, title: TASK_STATUS_LABELS[TASK_STATUS.NEW], items: [] },
                [TASK_STATUS.DOING]: { id: TASK_STATUS.DOING, title: TASK_STATUS_LABELS[TASK_STATUS.DOING], items: [] },
                [TASK_STATUS.OVERDUE]: { id: TASK_STATUS.OVERDUE, title: TASK_STATUS_LABELS[TASK_STATUS.OVERDUE], items: [] },
                [TASK_STATUS.DONE]: { id: TASK_STATUS.DONE, title: TASK_STATUS_LABELS[TASK_STATUS.DONE], items: [] }
            };

            fetchedTasks.forEach(task => {
                let badgeInfo = { text: 'Tarefa', color: 'primary' };
                if (task.type === TASK_TYPE.CONTACT) badgeInfo = { text: 'Contato', color: 'primary' };
                else if (task.type === TASK_TYPE.TECHNICAL) badgeInfo = { text: 'Avaliação/Treino', color: 'info' };
                else if (task.type === TASK_TYPE.FINANCIAL) badgeInfo = { text: 'Financeiro', color: 'success' };
                else if (task.type === TASK_TYPE.MAINTENANCE) badgeInfo = { text: 'Manutenção', color: 'warning' };
                else if (task.type === TASK_TYPE.INTERNAL) badgeInfo = { text: 'Interno', color: 'secondary' };

                let dateStr = '';
                if (task.scheduledDate) {
                    try {
                        let dateObj;
                        if (task.scheduledDate.includes('T')) {
                            dateObj = new Date(task.scheduledDate);
                        } else {
                            dateObj = new Date(task.scheduledDate + 'T' + (task.scheduledTime || '00:00:00'));
                        }
                        dateStr = format(dateObj, "dd/MM 'às' HH:mm", { locale: ptBR });
                    } catch (e) {
                        dateStr = task.scheduledDate;
                    }
                }

                const status = task.status || TASK_STATUS.NEW;

                if (status === 'archived') {
                    return; // Ignore archived tasks
                }

                const item = {
                    id: task.id,
                    type: task.type,
                    title: task.title,
                    description: task.description,
                    date: dateStr,
                    badgeInfo,
                    ...task
                };

                if (newColumns[status]) {
                    newColumns[status].items.push(item);
                } else {
                    newColumns[TASK_STATUS.NEW].items.push(item);
                }
            });

            setColumns(newColumns);
        } catch (error) {
            toast.error("Erro ao buscar tarefas");
            console.error("Erro em fetchTasks:", error);
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks, refreshKey]);

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const startColumnId = source.droppableId;
        const finishColumnId = destination.droppableId;

        const newColumns = { ...columns };
        const startColumn = newColumns[startColumnId];
        const finishColumn = newColumns[finishColumnId];

        const startItems = Array.from(startColumn.items);
        const [movedItem] = startItems.splice(source.index, 1);

        const finishItems = startColumnId === finishColumnId ? startItems : Array.from(finishColumn.items);
        finishItems.splice(destination.index, 0, movedItem);

        newColumns[startColumnId] = { ...startColumn, items: startItems };
        if (startColumnId !== finishColumnId) {
            newColumns[finishColumnId] = { ...finishColumn, items: finishItems };
        }

        setColumns(newColumns);

        if (startColumnId !== finishColumnId) {
            try {
                await TaskService.updateTaskStatus(idTenant, user, draggableId, finishColumnId);
            } catch (error) {
                toast.error("Erro ao atualizar status");
                fetchTasks();
            }
        }
    };

    return { columns, loading, onDragEnd, refetch: fetchTasks };
};

